import { useEffect, useState, useCallback } from "react";
import { Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/Page";
import { Modal, Label, inputCls, SKILLS } from "./shared";

export function GladiatorModal({ gladId, onClose, onChanged }) {
  const [g, setG] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/gladiators/${gladId}`).then((r) => {
    setG(r.data.gladiator);
    setForm({ name: r.data.gladiator.name, rating: r.data.gladiator.rating, potential: r.data.gladiator.potential, training_speed: r.data.gladiator.training_speed, skill_points: r.data.gladiator.skill_points, wins: r.data.gladiator.wins, losses: r.data.gladiator.losses, streak: r.data.gladiator.streak, retired: r.data.gladiator.retired, skills: { ...r.data.gladiator.skills }, owner_id: r.data.gladiator.owner_id });
  }).catch((e) => toast.error(apiErr(e))), [gladId]);
  useEffect(() => { load(); }, [load]);

  if (!form) return <Modal title="Gladiator" onClose={onClose} testid="gladiator-modal"><Loader /></Modal>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSkill = (s, v) => setForm((f) => ({ ...f, skills: { ...f.skills, [s]: Number(v) } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/gladiators/${gladId}`, { name: form.name, rating: Number(form.rating), potential: Number(form.potential), training_speed: Number(form.training_speed), skill_points: Number(form.skill_points), wins: Number(form.wins), losses: Number(form.losses), streak: Number(form.streak), retired: form.retired, skills: form.skills, owner_id: form.owner_id });
      toast.success("Gladiator updated"); onChanged(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete gladiator ${form.name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/gladiators/${gladId}`); toast.success("Gladiator deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Modal title={`Gladiator · ${g.name} (${g.cls})`} onClose={onClose} testid="gladiator-modal">
      {/* Age and Lifecycle Info */}
      <div className="mb-4 p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-zinc-500 uppercase text-[10px] mb-0.5">Age</div>
            <div className="text-zinc-100 font-semibold">{g.age} years</div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px] mb-0.5">Lifecycle</div>
            <div className="text-amber-300 font-semibold capitalize">{g.lifecycle?.replace('_', ' ')}</div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px] mb-0.5">Slow Decline</div>
            <div className="text-orange-400 font-semibold">{g.slow_decline_age} years</div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px] mb-0.5">Rapid Decline</div>
            <div className="text-red-400 font-semibold">{g.rapid_decline_age} years</div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-zinc-500">
          Decline ages are calculated from potential. Training speed: <span className="text-amber-400 font-semibold">{g.training_speed}</span> (default 1.35)
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Name</Label><input data-testid="g-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><Label>Rating</Label><input data-testid="g-rating" type="number" className={inputCls} value={form.rating} onChange={(e) => set("rating", e.target.value)} /></div>
        <div><Label>Potential · {g.potential_grade}</Label><input data-testid="g-potential" type="number" step="0.01" min="0" className={inputCls} value={form.potential} onChange={(e) => set("potential", e.target.value)} /></div>
        <div>
          <Label>Training Speed</Label>
          <input data-testid="g-training-speed" type="number" step="0.01" min="0.1" max="5" className={inputCls} value={form.training_speed} onChange={(e) => set("training_speed", e.target.value)} />
          <div className="text-[10px] text-zinc-500 mt-0.5">Default: 1.35 (leave as-is for normal speed)</div>
        </div>
        <div><Label>Skill Points</Label><input data-testid="g-sp" type="number" className={inputCls} value={form.skill_points} onChange={(e) => set("skill_points", e.target.value)} /></div>
        <div><Label>Wins</Label><input data-testid="g-wins" type="number" className={inputCls} value={form.wins} onChange={(e) => set("wins", e.target.value)} /></div>
        <div><Label>Losses</Label><input data-testid="g-losses" type="number" className={inputCls} value={form.losses} onChange={(e) => set("losses", e.target.value)} /></div>
        <div><Label>Win Streak</Label><input data-testid="g-streak" type="number" className={inputCls} value={form.streak} onChange={(e) => set("streak", e.target.value)} /></div>
        <div><Label>Owner User ID</Label><input data-testid="g-owner" className={inputCls} value={form.owner_id} onChange={(e) => set("owner_id", e.target.value)} /></div>
      </div>

      <div className="mt-5">
        <Label>Skills (1–1000)</Label>
        <div className="grid grid-cols-3 gap-2">
          {SKILLS.map((s) => (
            <div key={s}>
              <div className="text-[10px] uppercase text-zinc-500 mb-0.5">{s}</div>
              <input data-testid={`g-skill-${s}`} type="number" className={`${inputCls} px-2 py-1.5`} value={form.skills[s]} onChange={(e) => setSkill(s, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300 cursor-pointer">
        <input data-testid="g-retired" type="checkbox" checked={form.retired} onChange={(e) => set("retired", e.target.checked)} className="accent-amber-500 w-4 h-4" /> Retired
      </label>

      <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-zinc-800">
        <button data-testid="g-delete-btn" onClick={del} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-red-400"><Trash2 size={15} /> Delete gladiator</button>
        <button data-testid="g-save-btn" onClick={save} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-amber-400 disabled:opacity-50 rounded-sm"><Save size={15} /> Save</button>
      </div>
    </Modal>
  );
}
